'use client'

import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile widget — the client half of Supabase Auth CAPTCHA.
 *
 * Graceful degradation: when `NEXT_PUBLIC_TURNSTILE_SITE_KEY` is unset (the
 * state before CAPTCHA is turned on) this renders NOTHING and never produces a
 * token, so every auth form keeps working exactly as before. Flip it on by
 * setting the site key in the env + redeploying, THEN enabling CAPTCHA in the
 * Supabase dashboard (in that order — enabling Supabase CAPTCHA while the site
 * key is absent would 400 every auth call).
 *
 * The widget is single-use: bump `resetSignal` after each submit to request a
 * fresh token for the next attempt.
 */

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

/** True when a Turnstile site key is configured (CAPTCHA UI is active). */
export const CAPTCHA_ENABLED = Boolean(SITE_KEY)

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  reset: (id?: string) => void
  remove: (id?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    if (existing) {
      if (window.turnstile) resolve()
      else existing.addEventListener('load', () => resolve(), { once: true })
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Turnstile failed to load'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

export function Turnstile({
  onVerify,
  onExpire,
  onError,
  resetSignal = 0,
  action,
  className,
}: {
  onVerify: (token: string) => void
  onExpire?: () => void
  /**
   * Optional: the widget can't produce a token — the script was blocked or
   * failed to load, or Turnstile reported an error. Lets a form explain the
   * problem instead of waiting for a token that will never come. (An error
   * also still calls onExpire, as before.)
   */
  onError?: () => void
  /** Change this value (e.g. increment) to reset the widget for a new token. */
  resetSignal?: number
  /** Optional Turnstile "action" label for analytics (e.g. 'login'). */
  action?: string
  className?: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  // Keep the latest callbacks in refs so the render effect doesn't re-run
  // (and re-create the widget) every time the parent passes new closures.
  // Updated in an effect (not during render) per the rules of hooks — the
  // Turnstile callback only fires on user interaction, well after commit.
  const onVerifyRef = useRef(onVerify)
  const onExpireRef = useRef(onExpire)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onVerifyRef.current = onVerify
    onExpireRef.current = onExpire
    onErrorRef.current = onError
  })

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return
    let cancelled = false
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        if (widgetIdRef.current) return // already rendered
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          callback: (token: string) => onVerifyRef.current(token),
          'expired-callback': () => onExpireRef.current?.(),
          'error-callback': () => {
            onExpireRef.current?.()
            onErrorRef.current?.()
          },
        })
      })
      .catch(() => {
        // Script blocked / failed — leave the form usable. If CAPTCHA is
        // enforced, the server call returns a captcha error the user sees.
        if (!cancelled) onErrorRef.current?.()
      })
    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null
      }
    }
  }, [action])

  // Single-use tokens: reset the widget when the parent bumps resetSignal.
  useEffect(() => {
    if (resetSignal === 0) return
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current)
      } catch {
        /* ignore */
      }
    }
  }, [resetSignal])

  if (!SITE_KEY) return null
  return <div ref={containerRef} className={className} />
}
